import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import SupportHero from './components/SupportHero';
import ContactInfo from './components/ContactInfo';
import FAQSection from './components/FAQSection';
import ContactForm from './components/ContactForm';
import { injectJsonLd, setDocumentTitle, setMetaDescription, setCanonical } from '../../utils/seo';

export default function SupportPage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://jayeondameun.kr';

    setDocumentTitle('고객센터 | 자연담은 스피루리나 약손콩 건강식품 FAQ');
    setMetaDescription(
      '자연담은 고객센터 - 스피루리나, 약손콩 건강식품 주문, 배송, 교환/반품, 제품 성분 관련 자주 묻는 질문과 답변. 02-3487-2025로 문의하세요.',
    );
    setCanonical(`${siteUrl}/support`);

    const cleanup = injectJsonLd('jsonld-support', [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '자연담은 고객센터',
        url: `${siteUrl}/support`,
        description: '자연담은 스피루리나, 약손콩 건강식품 고객센터 - FAQ 및 1:1 문의',
        inLanguage: 'ko-KR',
        isPartOf: { '@type': 'WebSite', url: `${siteUrl}/`, name: '자연담은' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '홈', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: '고객센터', item: `${siteUrl}/support` },
          ],
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '02-3487-2025',
          email: 'homedr365@gmail.com',
          contactType: 'customer service',
          availableLanguage: 'Korean',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '주문 후 배송까지 얼마나 걸리나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '주문 확인 후 영업일 기준 1~2일 내 출고되며, 출고 후 1~2일 내 수령 가능합니다. 도서산간 지역은 추가 1~2일이 소요될 수 있습니다.',
            },
          },
          {
            '@type': 'Question',
            name: '배송비는 얼마인가요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '3만원 이상 구매 시 무료배송이며, 3만원 미만 구매 시 배송비 3,000원이 부과됩니다.',
            },
          },
          {
            '@type': 'Question',
            name: '주문 취소는 어떻게 하나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '출고 전까지는 고객센터(02-3487-2025) 또는 이메일(homedr365@gmail.com)로 연락 주시면 취소 처리해 드립니다. 출고 이후에는 반품 절차로 진행됩니다.',
            },
          },
          {
            '@type': 'Question',
            name: '약손콩은 어떤 제품인가요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '약손콩은 국내산 서리태(검은콩)를 주원료로 하여 스피루리나, 클로렐라 등 천연 성분을 배합한 건강기능식품입니다. 단백질, 식이섬유, 항산화 성분이 풍부합니다.',
            },
          },
          {
            '@type': 'Question',
            name: '하루 권장 섭취량은 얼마인가요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '성인 기준 하루 2~3회, 1회 1포(10g)를 물 또는 음료에 타서 드시는 것을 권장합니다. 개인 건강 상태에 따라 조절하실 수 있습니다.',
            },
          },
          {
            '@type': 'Question',
            name: '알레르기 성분이 있나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '대두(콩) 성분이 포함되어 있습니다. 콩 알레르기가 있으신 분은 섭취 전 전문의와 상담하시기 바랍니다. 자세한 성분 정보는 제품 라벨을 확인해 주세요.',
            },
          },
          {
            '@type': 'Question',
            name: '교환 및 반품 기간은 얼마나 되나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '수령일로부터 7일 이내에 교환 및 반품 신청이 가능합니다. 단, 식품 특성상 개봉 후에는 교환/반품이 불가합니다.',
            },
          },
          {
            '@type': 'Question',
            name: '불량품을 받았을 때는 어떻게 하나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '불량품 수령 시 사진과 함께 고객센터로 연락 주시면 신속하게 교환 또는 환불 처리해 드립니다. 이 경우 반품 배송비는 저희가 부담합니다.',
            },
          },
          {
            '@type': 'Question',
            name: '어떤 결제 수단을 사용할 수 있나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '신용카드, 체크카드, 계좌이체, 카카오페이, 네이버페이 등 다양한 결제 수단을 지원합니다.',
            },
          },
          {
            '@type': 'Question',
            name: '정기구독 할인이 있나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '정기구독 신청 시 매월 15% 할인 혜택을 드립니다. 구독 주기는 1개월, 2개월, 3개월 중 선택 가능하며 언제든지 변경/해지하실 수 있습니다.',
            },
          },
        ],
      },
    ]);

    return cleanup;
  }, []);

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
      <Navbar />
      <main>
        <SupportHero />
        <ContactInfo />
        <div id="faq">
          <FAQSection />
        </div>
        <div id="contact">
          <ContactForm />
        </div>
      </main>
      <PageFooter />
    </div>
  );
}
